import { create } from "zustand";
import {
  getAllHolidays,
  getPublicHolidays,
  getOptionalHolidays,
  getEmployeesForOptionalHoliday,
  addHoliday,
  updateHoliday,
  deleteHoliday,
  getAllEmployeeSelections,
  getHRSelectedOptionalHolidays,
  submitHRHolidaySelection,
  editEmployeeHoliday,
  cancelEmployeeHolidays,
  replaceEmployeeHolidays,
  bulkAddHolidays as bulkAddHolidaysService
} from "@/services/HrService/HolidayService";

const useHRHolidayStore = create((set, get) => ({
  allHolidays: null,
  publicHolidays: null,
  optionalHolidays: null,
  employeeSelections: null,
  selectedOptionalHolidays: null,
  selectedYear: null,
  loading: false,
  error: null,

  // Set selected year for filtering
  setSelectedYear: (year) => set({ selectedYear: year }),

  // Fetch all holidays (public and optional), optionally by year
  fetchAllHolidays: async (year = null) => {
    try {
      set({ loading: true, error: null });
      const yearToUse = year || get().selectedYear;
      const response = await getAllHolidays(yearToUse);
      console.log("All Holidays API Response:", response);

      if (response.success) {
        set({ allHolidays: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch all holidays', loading: false });
      }
    } catch (error) {
      console.error("Fetch All Holidays Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Fetch public holidays, optionally by year
  fetchPublicHolidays: async (year = null) => {
    try {
      set({ loading: true, error: null });
      const yearToUse = year || get().selectedYear;
      const response = await getPublicHolidays(yearToUse);
      console.log("Public Holidays API Response:", response);

      if (response.success) {
        set({ publicHolidays: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch public holidays', loading: false });
      }
    } catch (error) {
      console.error("Fetch Public Holidays Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Fetch optional holidays
  fetchOptionalHolidays: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getOptionalHolidays();
      console.log("Optional Holidays API Response:", response);

      if (response.success) {
        set({ optionalHolidays: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch optional holidays', loading: false });
      }
    } catch (error) {
      console.error("Fetch Optional Holidays Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Fetch all employee selections, optionally by year
  fetchAllEmployeeSelections: async (year = null) => {
    try {
      set({ loading: true, error: null });
      const yearToUse = year || get().selectedYear;
      const response = await getAllEmployeeSelections(yearToUse);
      console.log("Employee Selections API Response:", response);

      if (response.success) {
        set({ employeeSelections: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch employee selections', loading: false });
      }
    } catch (error) {
      console.error("Fetch Employee Selections Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Fetch employees for a specific optional holiday
  fetchEmployeesForHoliday: async (holidayId) => {
    try {
      set({ loading: true, error: null });
      const response = await getEmployeesForOptionalHoliday(holidayId);
      console.log("Employees For Holiday API Response:", response);

      if (response.success) {
        set({ loading: false });
        return response.data;
      } else {
        set({ error: response.error?.message || 'Failed to fetch employees', loading: false });
        return [];
      }
    } catch (error) {
      console.error("Fetch Employees For Holiday Error:", error);
      set({ error: error.message, loading: false });
      return [];
    }
  },

  // Add a new holiday
  addNewHoliday: async (holidayData) => {
    try {
      set({ loading: true, error: null });
      const response = await addHoliday(holidayData);
      console.log("Add Holiday API Response:", response);

      if (response.success) {
        // Refresh the holidays list
        await get().fetchAllHolidays();
        set({ loading: false });
        return { success: true, data: response.data };
      } else {
        set({ error: response.error?.message || 'Failed to add holiday', loading: false });
        return { success: false, error: response.error?.message };
      }
    } catch (error) {
      console.error("Add Holiday Error:", error);
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  // Update an existing holiday
  updateExistingHoliday: async (holidayId, updateData) => {
    try {
      set({ loading: true, error: null });
      const response = await updateHoliday(holidayId, updateData);
      console.log("Update Holiday API Response:", response);

      if (response.success) {
        // Refresh the holidays list
        await get().fetchAllHolidays();
        set({ loading: false });
        return { success: true, data: response.data };
      } else {
        set({ error: response.error?.message || 'Failed to update holiday', loading: false });
        return { success: false, error: response.error?.message };
      }
    } catch (error) {
      console.error("Update Holiday Error:", error);
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  // Delete a holiday
  deleteExistingHoliday: async (holidayId) => {
    try {
      set({ loading: true, error: null });
      const response = await deleteHoliday(holidayId);
      console.log("Delete Holiday API Response:", response);

      if (response.success) {
        // Refresh the holidays list
        await get().fetchAllHolidays();
        set({ loading: false });
        return { success: true };
      } else {
        set({ error: response.error?.message || 'Failed to delete holiday', loading: false });
        return { success: false, error: response.error?.message };
      }
    } catch (error) {
      console.error("Delete Holiday Error:", error);
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  // Fetch HR's selected optional holidays
  fetchSelectedOptionalHolidays: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getHRSelectedOptionalHolidays();
      console.log("HR Selected Optional Holidays API Response:", response);

      if (response.success) {
        set({ selectedOptionalHolidays: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch selected holidays', loading: false });
      }
    } catch (error) {
      console.error("Fetch Selected Optional Holidays Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Submit HR's holiday selection
  submitHolidaySelection: async (holidayIds) => {
    try {
      set({ loading: true, error: null });
      const response = await submitHRHolidaySelection(holidayIds);
      console.log("Submit HR Holiday Selection API Response:", response);

      if (response.success) {
        set({ loading: false });
        return { success: true, data: response.data };
      } else {
        set({ error: response.error?.message || 'Failed to submit holiday selection', loading: false });
        return { success: false, error: response.error?.message };
      }
    } catch (error) {
      console.error("Submit HR Holiday Selection Error:", error);
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  // Bulk add holidays
  bulkAddHolidays: async (holidaysArray) => {
    try {
      set({ loading: true, error: null });
      const response = await bulkAddHolidaysService(holidaysArray);
      console.log("Bulk Add Holidays API Response:", response);

      if (response.success) {
        // Refresh the holidays list for the selected year
        await get().fetchAllHolidays();
        set({ loading: false });
        return { success: true, data: response.data };
      } else {
        set({ error: response.error?.message || 'Failed to bulk add holidays', loading: false });
        return { success: false, error: response.error?.message };
      }
    } catch (error) {
      console.error("Bulk Add Holidays Error:", error);
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  // Reset errors
  resetErrors: () => set({ error: null }),

  // Edit employee's holiday selections (bulk remove and replace)
  editEmployeeHolidaySelection: async (userId, removeHolidayIds, addHolidayIds) => {
    try {
      set({ loading: true, error: null });
      const response = await editEmployeeHoliday(userId, removeHolidayIds, addHolidayIds);
      console.log("Edit Employee Holiday API Response:", response);

      if (response.success) {
        // Refresh employee selections after successful edit
        await get().fetchAllEmployeeSelections();
        set({ loading: false });
        return { success: true, data: response.data };
      } else {
        set({ error: response.error?.message || 'Failed to edit employee holidays', loading: false });
        return { success: false, error: response.error?.message };
      }
    } catch (error) {
      console.error("Edit Employee Holiday Error:", error);
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  // Cancel employee's optional holidays (HR function)
  cancelEmployeeOptionalHolidays: async (userId, holidayIds) => {
    try {
      set({ loading: true, error: null });
      const response = await cancelEmployeeHolidays(userId, holidayIds);
      console.log("Cancel Employee Holiday API Response:", response);

      if (response.success) {
        // Refresh employee selections after successful cancellation
        await get().fetchAllEmployeeSelections();
        set({ loading: false });
        return { success: true, data: response.data };
      } else {
        set({ error: response.error?.message || 'Failed to cancel employee holidays', loading: false });
        return { success: false, error: response.error?.message };
      }
    } catch (error) {
      console.error("Cancel Employee Holiday Error:", error);
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  // Replace employee's optional holidays (HR function)
  replaceEmployeeOptionalHolidays: async (userId, cancelHolidayIds, addHolidayIds) => {
    try {
      set({ loading: true, error: null });
      const response = await replaceEmployeeHolidays(userId, cancelHolidayIds, addHolidayIds);
      console.log("Replace Employee Holiday API Response:", response);

      if (response.success) {
        // Refresh employee selections after successful replacement
        await get().fetchAllEmployeeSelections();
        set({ loading: false });
        return { success: true, data: response.data };
      } else {
        set({ error: response.error?.message || 'Failed to replace employee holidays', loading: false });
        return { success: false, error: response.error?.message };
      }
    } catch (error) {
      console.error("Replace Employee Holiday Error:", error);
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  // Reset all data
  resetHolidayData: () => set({
    allHolidays: null,
    publicHolidays: null,
    optionalHolidays: null,
    employeeSelections: null,
    selectedOptionalHolidays: null,
    selectedYear: null,
    loading: false,
    error: null
  }),
}));

export default useHRHolidayStore;